import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'

import { getListAlertsQueryKey } from '@/api/alerts.hook'
import {
  getGetOngoingAssessmentQueryKey,
  getListOngoingAssessmentsQueryKey,
} from '@/api/assessments.hook'
import { ASYNC_CASCADE_REFETCH_DELAY_MS } from '@/api/consts'
import { getListControlsQueryKey } from '@/api/control.hook'
import { getListDiscoverySolutionsQueryKey } from '@/api/discovery.hook'
import { getListSignalsQueryKey } from '@/api/signals.hook'
import {
  autocompleteBusinessOwner,
  createBusinessOwner,
  createCompany,
  createOrgCompanyEngagement,
  deleteOrgCompany,
  deleteOrgCompanyEngagement,
  getCompany,
  getCompanyActivityLog,
  getCompanyDrawerMetadata,
  getCompanySubprocessors,
  getEngagement,
  getInherentRisk,
  getPointsOfContact,
  getPointsOfContactsByCompany,
  linkSolutionsToCompany,
  listAggregatedSubprocessors,
  listCompanies,
  listCompaniesDataTypes,
  listEngagements,
  listInherentRiskCategories,
  overrideCompanyAIProfile,
  reanalyzeCompanySubprocessors,
  reclassifyCompanyAIProfile,
  reprojectOrgCompany,
  reprojectOrgCompanyEngagement,
  searchEngagementCandidates,
  setIRQ,
  updateBusinessOwner,
  updateCompany,
  updateCompanyInherentRisk,
  updateEngagementInherentRisk,
  updateOrgCompanyEngagement,
  updateOrgCompanyReAssessmentDueDate,
  updatePointOfContact,
} from '@/gen/inventory/v1/company_service-CompanyInventoryService_connectquery'
import {
  Company,
  CompanyInventoryService,
  GetInherentRiskRequest,
  SearchEngagementCandidatesRequest,
} from '@/gen/inventory/v1/company_service_pb'
import { CompanyOrEngagement } from '@/gen/inventory/v1/entity_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { companyStatusLabel, riskLevelLabel } from '@/const/label'

import { useFeatureFlagEnabled } from '@/lib/config/featureflag'
import { defaultTransport } from '@/lib/connect/connect-transport'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

export const companyInventoryClient = createClient(CompanyInventoryService, defaultTransport)

// --- Query Key Factories ---

export const getListCompaniesQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.listCompanies,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetCompanyQueryKey = (id?: string) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getCompany,
    input: { id },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getSearchEngagementCandidatesQueryKey = (vendorName?: string) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.searchEngagementCandidates,
    input: { vendorName },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetCompanyDrawerMetadataQueryKey = (
  companyId?: string,
  solutionId?: string,
  withUsers?: boolean,
) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getCompanyDrawerMetadata,
    input: { companyId, solutionId, withUsers },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getListCompaniesDataTypesQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.listCompaniesDataTypes,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getListInherentRiskCategoriesQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.listInherentRiskCategories,
    cardinality: 'finite',
    transport: defaultTransport,
  })

export const getGetCompanyActivityLogQueryKey = (companyId?: string) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getCompanyActivityLog,
    input: { companyId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetPointsOfContactQueryKey = (entity: CompanyOrEngagement) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getPointsOfContact,
    input: { entity },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getPointsOfContactsByCompanyQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getPointsOfContactsByCompany,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getAutocompleteBusinessOwnerQueryKey = (companyId: string, search?: string) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.autocompleteBusinessOwner,
    input: { companyId, search },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetInherentRiskQueryKey = (companyId?: string, orgCompanyEngagementId?: string) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getInherentRisk,
    input: { companyId, orgCompanyEngagementId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetEngagementQueryKey = (engagementId?: string) =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getEngagement,
    input: { engagementId: engagementId ?? '' },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getListEngagementsQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.listEngagements,
    transport: defaultTransport,
    cardinality: 'finite',
  })

// --- Hooks ---

export const useCreateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation(createCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey() })
    },
  })
}

export const useGetCompany = (companyId?: string, enabled = true) => {
  return useQuery(
    getCompany,
    { id: companyId },
    {
      enabled: enabled && !!companyId,
      refetchInterval: (query) =>
        query.state.data?.company?.calculatedRisk !== RiskLevel.UNSPECIFIED ? false : 5000,
      refetchIntervalInBackground: true,
    },
  )
}

export const useGetCompanyDrawerMetadata = (
  companyId?: string,
  solutionId?: string,
  enabled = true,
) => {
  const { isEnabled } = useFeatureFlagEnabled('third-party-usage-list-users')
  return useQuery(
    getCompanyDrawerMetadata,
    { companyId, solutionId, withUsers: isEnabled },
    {
      enabled: enabled && !!companyId,
    },
  )
}

export const useListCompaniesDataTypes = () => {
  const queryState = useQuery(listCompaniesDataTypes)

  return {
    ...queryState,
    data: queryState.data?.companiesDataTypes || [],
  }
}

export const useListCompanies = (enabled = true) => {
  const queryState = useQuery(listCompanies, {}, { enabled })

  return {
    ...queryState,
    data: enabled ? queryState.data?.companies || [] : [],
  }
}

export const useSearchEngagementCandidates = (
  req: Pick<SearchEngagementCandidatesRequest, 'vendorName'>,
  enabled = true,
) => {
  const queryState = useQuery(searchEngagementCandidates, req, {
    enabled: enabled && !!req.vendorName,
  })

  return {
    ...queryState,
    data: queryState.data?.engagements || [],
  }
}

export const useGetInherentRisk = (req: GetInherentRiskRequest, enabled = true) => {
  const queryState = useQuery(getInherentRisk, req, { enabled })
  return {
    ...queryState,
    data: queryState.data?.inherentRiskGroups || [],
    categoryDisplayOrders: queryState.data?.categoryDisplayOrders || [],
    isTemplateOutdated: queryState.data?.isTemplateOutdated ?? false,
  }
}

export const useListInherentRiskCategories = () => {
  const queryState = useQuery(listInherentRiskCategories)
  return {
    ...queryState,
    data: queryState.data?.categories || [],
    categoryDisplayOrders: queryState.data?.categoryDisplayOrders || [],
  }
}

export const useGetCompanyActivityLog = (companyId: string) => {
  const queryState = useQuery(getCompanyActivityLog, { companyId })
  return {
    ...queryState,
    data: queryState.data?.items || [],
  }
}

export const useLinkSolutionsToCompany = () => {
  const queryClient = useQueryClient()

  return useMutation(linkSolutionsToCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListDiscoverySolutionsQueryKey() })
    },
  })
}

const useInvalidateAfterCompanyMutation = (companyId: Company['id']) => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: getListAlertsQueryKey() })
    queryClient.invalidateQueries({ queryKey: getListSignalsQueryKey() })
    queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
    queryClient.invalidateQueries({ queryKey: getGetCompanyActivityLogQueryKey(companyId) })
    queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey() })

    // calculateAllControlsForCompany runs concurrently after the company is updated
    // and takes a few seconds; wait before invalidating the downstream queries.
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetOngoingAssessmentQueryKey() })
    }, ASYNC_CASCADE_REFETCH_DELAY_MS)
  }
}

export const useUpdateCompany = (companyId: Company['id']) => {
  const invalidate = useInvalidateAfterCompanyMutation(companyId)
  const { showCompanyToast } = useUpdateCompanyToast()

  return useMutation(updateCompany, {
    onSuccess: (_, { status }) => {
      invalidate()
      if (status) {
        showCompanyToast('status', 'success', companyStatusLabel[status])
      }
    },
    onError: (_, { status }) => {
      if (status) {
        showCompanyToast('status', 'error', companyStatusLabel[status])
      }
    },
  })
}

export const useUpdateCompanyInherentRisk = (companyId: Company['id']) => {
  const invalidate = useInvalidateAfterCompanyMutation(companyId)
  const { showCompanyToast } = useUpdateCompanyToast()

  return useMutation(updateCompanyInherentRisk, {
    onSuccess: (_, { riskLevel }) => {
      invalidate()
      if (riskLevel !== undefined) {
        showCompanyToast('inherent risk', 'success', riskLevelLabel[riskLevel])
      }
    },
    onError: (_, { riskLevel }) => {
      if (riskLevel !== undefined) {
        showCompanyToast('inherent risk', 'error', riskLevelLabel[riskLevel])
      }
    },
  })
}

const useUpdateCompanyToast = () => {
  const { toast } = useToast()

  const showCompanyToast = (
    entity: 'inherent risk' | 'status',
    status: 'success' | 'error',
    newValue: string,
  ) => {
    const entityLabel = _.startCase(entity)
    const title =
      status === 'success' ? `${entityLabel} successfully changed` : `${entityLabel} update failed`

    const description =
      status === 'success'
        ? `This third party's ${entity} has been successfully set to "${newValue}".`
        : `Failed to update this third party's ${entity}.`

    toast({ title, description, variant: status == 'success' ? 'success' : 'destructive' })
  }

  return { showCompanyToast }
}

export const useAutocompleteBusinessOwner = (companyId: string, search: string) => {
  const queryState = useQuery(
    autocompleteBusinessOwner,
    { companyId, search },
    {
      placeholderData: (previousData) => previousData,
    },
  )
  return {
    ...queryState,
    businessOwners: queryState.data?.businessOwners || [],
  }
}

export const useGetPointsOfContact = (entity: CompanyOrEngagement) => {
  return useQuery(getPointsOfContact, { entity })
}

export const usePointsOfContactsByCompany = (enabled = true) => {
  const queryState = useQuery(getPointsOfContactsByCompany, {}, { enabled })
  return {
    ...queryState,
    data: queryState.data?.pointsOfContactByCompany || {},
  }
}

export const useUpdateBusinessOwner = (entity: CompanyOrEngagement) => {
  const queryClient = useQueryClient()

  return useMutation(updateBusinessOwner, {
    onSuccess: () => {
      switch (entity.entity?.case) {
        case 'companyId':
          queryClient.invalidateQueries({
            queryKey: getGetCompanyQueryKey(entity.entity.value),
          })
          queryClient.invalidateQueries({
            queryKey: getGetCompanyDrawerMetadataQueryKey(entity.entity.value),
          })
          queryClient.invalidateQueries({
            queryKey: getGetCompanyActivityLogQueryKey(entity.entity.value),
          })
          queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
          break
        case 'engagementId':
          queryClient.invalidateQueries({
            queryKey: getGetEngagementQueryKey(entity.entity.value),
          })
          queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
          break
      }
    },
  })
}

export const useCreateBusinessOwner = (entity: CompanyOrEngagement) => {
  const queryClient = useQueryClient()

  return useMutation(createBusinessOwner, {
    onSuccess: () => {
      switch (entity.entity?.case) {
        case 'companyId':
          queryClient.invalidateQueries({
            queryKey: getAutocompleteBusinessOwnerQueryKey(entity.entity.value),
          })
          break
        case 'engagementId':
          queryClient.invalidateQueries({
            queryKey: getAutocompleteBusinessOwnerQueryKey(entity.entity.value),
          })
          break
      }
    },
  })
}

export const useUpdatePointOfContact = (entity: CompanyOrEngagement) => {
  const queryClient = useQueryClient()

  return useMutation(updatePointOfContact, {
    onSuccess: () => {
      switch (entity.entity?.case) {
        case 'companyId':
          queryClient.invalidateQueries({
            queryKey: getGetPointsOfContactQueryKey(entity),
          })
          queryClient.invalidateQueries({
            queryKey: getPointsOfContactsByCompanyQueryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: getGetCompanyActivityLogQueryKey(entity.entity.value),
          })
          break
        case 'engagementId':
          queryClient.invalidateQueries({
            queryKey: getGetPointsOfContactQueryKey(entity),
          })
          queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
          break
      }
    },
  })
}

export const useCreateOrgCompanyEngagement = (companyId: string) => {
  const queryClient = useQueryClient()
  return useMutation(createOrgCompanyEngagement, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getSearchEngagementCandidatesQueryKey() })
    },
  })
}

export const useUpdateOrgCompanyEngagement = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation(updateOrgCompanyEngagement, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetEngagementQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyActivityLogQueryKey(companyId) })
    },
  })
}

export const useUpdateEngagementInherentRisk = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation(updateEngagementInherentRisk, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetEngagementQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyActivityLogQueryKey(companyId) })

      // Engagement validated risk cascades to the company's calculated risk via
      // Kafka, so the first invalidate may race ahead of the calculator —
      // re-invalidate after the cascade has had time to land.
      const invalidateCompanyRisk = () => {
        queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
        queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
        queryClient.invalidateQueries({
          queryKey: getGetCompanyDrawerMetadataQueryKey(companyId),
        })
        queryClient.invalidateQueries({
          queryKey: getGetInherentRiskQueryKey(companyId, variables.id),
        })
      }
      invalidateCompanyRisk()
      setTimeout(invalidateCompanyRisk, ASYNC_CASCADE_REFETCH_DELAY_MS)
    },
  })
}

export const useDeleteOrgCompanyEngagement = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation(deleteOrgCompanyEngagement, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getGetInherentRiskQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetEngagementQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
    },
  })
}

export const useReprojectOrgCompany = () => {
  const queryClient = useQueryClient()

  return useMutation(reprojectOrgCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
    },
  })
}

export const useReprojectOrgCompanyEngagement = () => {
  const queryClient = useQueryClient()

  return useMutation(reprojectOrgCompanyEngagement, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetEngagementQueryKey() })
    },
  })
}

export const useGetEngagement = (engagementId: string, enabled = true) => {
  return useQuery(
    getEngagement,
    { engagementId },
    {
      enabled: enabled && !!engagementId,
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
    },
  )
}

export const useListEngagements = () => {
  return useQuery(
    listEngagements,
    {},
    {
      enabled: true,
    },
  )
}

export const useUpdateOrgCompanyReAssessmentDueDate = (companyId: string) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(updateOrgCompanyReAssessmentDueDate, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      toast({
        title: 'Due date updated',
        description: 'The re-assessment due date has been updated.',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Failed to update due date',
        description: 'An error occurred while updating the due date.',
        variant: 'destructive',
      })
    },
  })
}

export const useGetCompanySubprocessors = (companyId?: string, enabled = true) => {
  const queryState = useQuery(
    getCompanySubprocessors,
    { companyId: companyId ?? '' },
    { enabled: enabled && !!companyId },
  )
  return {
    ...queryState,
    data: queryState.data?.subprocessors || [],
  }
}

export const useReanalyzeCompanySubprocessors = () => {
  const { toast } = useToast()

  return useMutation(reanalyzeCompanySubprocessors, {
    onSuccess: () => {
      toast({
        title: 'Subprocessor reanalysis queued',
        description:
          'Reanalysis can take a few minutes — refresh the page to see the updated fourth-parties.',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Failed to queue subprocessor reanalysis',
        description: 'An error occurred while requesting the reanalysis.',
        variant: 'destructive',
      })
    },
  })
}

// AI posture and capabilities live on the GLOBAL catalog company, so these overrides change what
// every tenant sees. Narrower than useInvalidateAfterCompanyMutation on purpose: alerts, signals,
// controls and ongoing assessments do not move when a vendor's AI classification does.
const useInvalidateAfterCompanyAIMutation = (companyId?: Company['id']) => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
    queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey() })
  }
}

export const useOverrideCompanyAIProfile = (companyId?: Company['id']) => {
  const invalidate = useInvalidateAfterCompanyAIMutation(companyId)

  return useMutation(overrideCompanyAIProfile, { onSuccess: () => invalidate() })
}

// Invalidates nothing: the classifier run is asynchronous and minutes long, so there is nothing
// new to read back yet.
export const useReclassifyCompanyAIProfile = () => useMutation(reclassifyCompanyAIProfile)

export const useListAggregatedSubprocessors = (enabled = true) => {
  const queryState = useQuery(listAggregatedSubprocessors, {}, { enabled })
  return {
    ...queryState,
    data: queryState.data?.aggregatedSubprocessors || [],
  }
}

export const useSetIRQ = (companyId?: string, engagementId?: string) => {
  const queryClient = useQueryClient()

  return useMutation(setIRQ, {
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyActivityLogQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListInherentRiskCategoriesQueryKey() })
      queryClient.invalidateQueries({
        queryKey: getGetInherentRiskQueryKey(companyId, engagementId),
      })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompaniesDataTypesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetEngagementQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListEngagementsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey(companyId) })
    },
  })
}

export const useDeleteOrgCompany = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(deleteOrgCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyDrawerMetadataQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      toast({
        title: 'Third-party deleted',
        description: 'The third-party has been permanently deleted.',
        variant: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete third-party',
        description: error.rawMessage || 'An error occurred while deleting the third-party.',
        variant: 'destructive',
      })
    },
  })
}
